import clsx from "clsx";
import { format } from "date-fns";
import { useState } from "react";
import { useForm } from "react-hook-form";
import TagManager from "react-gtm-module";

import Button from "../button";
import Input from "../input";
import Modal from "../modal";
import Select from "../select";
import Textarea from "../textarea";
import DateSelect from "../date-select";
import DateTimeSelect from "../date-time-select";
import ConfirmationModal from "../confirmation-modal";
import { activeCampaignSubmit } from "../../lib/forms";

const formatDate = (day) => {
  return format(day, "yyyy-MM-dd");
};

// Active Campaign field names.
const fieldMap = {
  bookingDate: "field[15]",
  bookingTime: "field[16]",
  location: "field[63]",
  budget: "field[112]",
  shootType: "field[29]",
  attendees: "field[64]",
  details: "field[17]",
  subscribe: "field[24][]"
};

const budgetChoices = [
  "$0 - $5,000",
  "$5,000 - $10,000",
  "$10,000 - $25,000",
  "$25,000+"
];

const PhotoshootFormModal = ({ locations, ...props }) => {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const { register, formState, handleSubmit, watch, getValues, setValue } =
    useForm({
      mode: "onChange",
      // Default Active Campaign values for this
      // specific form.
      defaultValues: {
        u: "6",
        f: "6",
        s: "0",
        c: "0",
        m: "0",
        act: "sub",
        v: "2",
        [fieldMap.bookingDate]: ""
      }
    });

  watch();

  const onSubmit = (data) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "contact_us",
        type: "shoot",
        "type-shoot": data[fieldMap.shootType],
        "crew-size": data[fieldMap.attendees],
        budget: data[fieldMap.budget],
        location: data[fieldMap.location],
        firstname: data.firstname,
        lastname: data.lastname,
        phone: data.phone,
        email: data.email
      }
    });

    activeCampaignSubmit(data, false)
      .then(() => setSubmitted(true))
      .catch((e) => setError(e));
  };
  const onError = (e) => console.log(e);

  const { errors } = formState;

  const bookingDateField = fieldMap.bookingDate.split("[")[1].slice(0, -1);

  const getError = (field) => {
    const fieldNum = field.split("[")[1].slice(0, -1);

    if (errors.field && errors.field[fieldNum]) {
      return errors.field[fieldNum].message;
    }

    return null;
  };

  if (submitted) {
    return <ConfirmationModal {...props} />;
  }

  if (error) {
    return (
      <Modal {...props}>
        <>
          <h2 className="font-bold text-2xl text-center">
            Something went wrong!
          </h2>

          <p className="text-md my-4 text-center font-light">
            {error?.message}
          </p>
        </>
      </Modal>
    );
  }

  return (
    <Modal {...props}>
      <form className={clsx("p-6")} onSubmit={handleSubmit(onSubmit, onError)}>
        <h2 className="font-bold text-3xl text-center">
          Interested in booking a
          <br /> photo or video shoot?
        </h2>

        <div className="my-4">
          <div className="form-group-inline">
            <Input
              type="text"
              placeholder="First name *"
              {...register("firstname", {
                required: "First name is required"
              })}
              errorText={errors.firstname && errors.firstname.message}
              inline
            />
            <Input
              type="text"
              placeholder="Last name *"
              {...register("lastname", { required: "Last name is required" })}
              errorText={errors.lastname && errors.lastname.message}
              inline
            />
          </div>

          <Input
            type="text"
            placeholder="Work email address *"
            {...register("email", { required: "Email is required" })}
            errorText={errors.email && errors.email.message}
          />

          <Input
            type="text"
            placeholder="Phone number *"
            {...register("phone", {
              required: "Phone number is required"
            })}
            errorText={errors.phone && errors.phone.message}
          />
          <Input
            type="text"
            placeholder="Company name *"
            {...register("customer_account", {
              required: "Company name is required"
            })}
            errorText={
              errors.customer_account && errors.customer_account.message
            }
          />

          <div className="form-group-inline">
            <DateSelect
              {...register(fieldMap.bookingDate, {
                required: "Booking date is required"
              })}
              placeholder="Desired Event Date *"
              selectedDate={getValues().field?.[bookingDateField]}
              disabledDates={(day) => day < new Date()}
              onChange={(day) => {
                setValue(fieldMap.bookingDate, formatDate(day));
              }}
              errorText={getError(fieldMap.bookingDate)}
              inline
            />

            <DateTimeSelect
              {...register(fieldMap.bookingTime, {
                required: "Booking time is required"
              })}
              date={getValues().field?.[bookingDateField]}
              type="text"
              placeholder="Desired Booking Time *"
              onChange={(e) => {
                setValue(fieldMap.bookingTime, e.target.value);
              }}
              errorText={getError(fieldMap.bookingTime)}
              inline
            />
          </div>

          <Select
            {...register(fieldMap.location, {
              required: "Location is required"
            })}
            errorText={getError(fieldMap.location)}
          >
            <option value="" disabled selected>
              Select your location of interest *
            </option>
            {locations.map((location) => (
              <option
                value={`${location.address1}, ${location.city} ${location.province}`}
                key={location.slug}
              >
                {location.address1}, {location.city} {location.province}
              </option>
            ))}
          </Select>

          <Select
            {...register(fieldMap.budget, {
              required: "Shoot Budget is required"
            })}
            errorText={getError(fieldMap.budget)}
          >
            <option value="" disabled selected>
              Shoot Budget *
            </option>
            {budgetChoices.map((choice) => (
              <option value={choice} key={choice}>
                {choice}
              </option>
            ))}
          </Select>

          <Select
            {...register(fieldMap.shootType, {
              required: "Type of shoot is required"
            })}
            errorText={getError(fieldMap.shootType)}
          >
            <option value="" disabled selected>
              Select the type of shoot *
            </option>
            <option value="Photoshoot">Photoshoot</option>
            <option value="Video Shoot">Video Shoot</option>
          </Select>

          <Input
            type="text"
            placeholder="Crew size *"
            {...register(fieldMap.attendees, {
              required: "Crew size is required"
            })}
            errorText={getError(fieldMap.attendees)}
          />

          <Textarea
            type="text"
            placeholder="Shoot description and any additional information"
            rows="3"
            {...register(fieldMap.details)}
          />
        </div>

        <Button type="submit" className="w-full my-4" outline>
          Book
        </Button>
      </form>
    </Modal>
  );
};

export default PhotoshootFormModal;
